(function () {
  'use strict';

  const CART_CREATE_MUTATION = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const CART_GET_QUERY = `
    query cartGet($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product { title }
                }
              }
              sellingPlanAllocation {
                sellingPlan { id name }
              }
              attributes { key value }
              ... on CartLine { parentRelationship { __typename } }
            }
          }
        }
      }
    }
  `;

  const CART_TOTAL_QUERY = `
    query cartTotal($cartId: ID!) {
      cart(id: $cartId) {
        cost { totalAmount { amount } }
      }
    }
  `;

  const CART_LINES_ADD_MUTATION = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          lines(first: 100) {
            edges {
              node {
                id
                attributes { key value }
                cost { totalAmount { amount } }
              }
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const CART_LINES_REMOVE_MUTATION = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        userErrors {
          field
          message
        }
      }
    }
  `;

  // Propiedades con las que BOGO+ marca la línea de regalo. Su descuento "Regalo" solo deja
  // el regalo a 0 € si las lleva, y valida en servidor que el tramo corresponde al importe.
  const BOGO_CAMPAIGN_KEY = '_BOGO+';
  const BOGO_TIER_KEY     = '_BOGO+_tier';

  class WimStorefrontClient {
    constructor({ storefrontToken, shop, apiVersion = '2025-10' } = {}) {
      this._token   = storefrontToken;
      this._shop    = shop || (window.Shopify && window.Shopify.shop);
      this._version = apiVersion;
    }

    _url() {
      return `https://${this._shop}/api/${this._version}/graphql.json`;
    }

    async _request(query, variables = {}) {
      const res = await fetch(this._url(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this._token,
        },
        body: JSON.stringify({ query: query.trim(), variables }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Storefront API HTTP ${res.status}: ${text}`);
      }
      const json = await res.json();
      if (json.errors && json.errors.length) {
        console.error('[WimStorefrontClient] GraphQL errors:', json.errors);
        throw new Error(json.errors[0].message);
      }
      return json.data;
    }

    toVariantGid(id) {
      if (id == null) return null;
      const s = String(id);
      return s.startsWith('gid://') ? s : `gid://shopify/ProductVariant/${s}`;
    }

    toSellingPlanGid(id) {
      if (id == null) return null;
      const s = String(id);
      return s.startsWith('gid://') ? s : `gid://shopify/SellingPlan/${s}`;
    }

    toAttributeList(obj) {
      return Object.entries(obj || {}).map(([key, value]) => ({ key, value: String(value) }));
    }

    async cartCreate(lines, cartAttributes = []) {
      const input = { lines };
      if (cartAttributes.length) input.attributes = cartAttributes;
      const data = await this._request(CART_CREATE_MUTATION, { input });
      const { cart, userErrors } = data.cartCreate;
      if (userErrors && userErrors.length) throw new Error(userErrors[0].message);
      return cart; // { id, checkoutUrl }
    }

    async cartGet(cartId) {
      const data = await this._request(CART_GET_QUERY, { cartId });
      return data.cart;
    }

    async createWizardCart({ machine, coffees = [], accessories = [], attributes = {} }) {
      const groupId   = `g-${Date.now()}`;
      const parentGid = machine ? this.toVariantGid(machine.variantId) : null;
      const lines     = [];

      if (machine) {
        lines.push({
          merchandiseId: parentGid,
          quantity: machine.quantity || 1,
        });
      }

      for (const c of coffees) {
        const line = {
          merchandiseId: this.toVariantGid(c.variantId),
          quantity: c.quantity || 1,
        };
        if (parentGid) line.parent = { merchandiseId: parentGid };
        if (c.sellingPlanId) line.sellingPlanId = this.toSellingPlanGid(c.sellingPlanId);
        lines.push(line);
      }

      for (const a of accessories) {
        const line = {
          merchandiseId: this.toVariantGid(a.variantId),
          quantity: a.quantity || 1,
        };
        if (parentGid) line.parent = { merchandiseId: parentGid };
        if (a.sellingPlanId) line.sellingPlanId = this.toSellingPlanGid(a.sellingPlanId);
        lines.push(line);
      }

      if (!lines.length) throw new Error('No lines to checkout');

      const cartAttributes = this.toAttributeList({ _fromWizard: 'true', ...attributes });

      // Shopify Storefront API devuelve líneas en orden inverso al de inserción.
      // Invertimos antes de enviar para que en checkout aparezcan: máquina, cafés, accesorios.
      const cart = await this.cartCreate(lines.reverse(), cartAttributes);

      // Log diagnóstico: muestra el estado real del carrito tras la creación
      // this.cartGet(cart.id).then(cartData => {
      //   console.group('[WimStorefrontClient] Cart created');
      //   console.log('checkoutUrl:', cartData.checkoutUrl);
      //   cartData.lines.edges.forEach(({ node }, i) => {
      //     console.log(
      //       `#${i + 1}`,
      //       node.merchandise.product.title,
      //       '—', node.merchandise.title,
      //       `x${node.quantity}`,
      //       node.sellingPlanAllocation ? `(plan: ${node.sellingPlanAllocation.sellingPlan.name})` : '',
      //       node.parentRelationship ? `(has parentRelationship)` : '(no parent)',
      //       '\n    attrs:', node.attributes
      //     );
      //   });
      //   console.groupEnd();
      // }).catch(() => {});

      return cart;
    }

    /** Devuelve la campaña de regalo por importe de BOGO+ activa ahora, o null. */
    activeGoalOrderCampaign(campaigns, now = Date.now()) {
      return (campaigns || []).find(c =>
        c.type === 'goal-order' &&
        c.enable_discount &&
        Number(c.starts_at) <= now &&
        (!c.ends_at || now < Number(c.ends_at))
      ) || null;
    }

    /** Elige el tramo más alto que alcanza el importe; BOGO+ solo regala el de ese tramo. */
    goalOrderTier(campaign, amount) {
      let match = null;
      (campaign.order_discount_tiers || []).forEach((tier, index) => {
        if (tier.type === 'gift' && amount >= Number(tier.trigger_value)) match = { tier, index };
      });
      return match;
    }

    /**
     * Añade al carrito el regalo de la campaña BOGO+ activa, como hace la app en el carrito de la
     * tienda online (a cuyo script no llega el carrito del Wizard). Si el descuento no lo deja a 0 €
     * (sin stock, fuera de mercado, promo caducada…) lo quita: nunca se cobra el regalo.
     * Devuelve true si el regalo quedó en el carrito.
     */
    async addBogoGift(cartId, campaigns) {
      const campaign = this.activeGoalOrderCampaign(campaigns);
      if (!campaign) return false;

      const { cart } = await this._request(CART_TOTAL_QUERY, { cartId });
      const match = this.goalOrderTier(campaign, Number(cart.cost.totalAmount.amount));
      const variant = match && match.tier.gift_products?.products?.[0]?.variants?.[0];
      if (!variant) return false;

      const data = await this._request(CART_LINES_ADD_MUTATION, {
        cartId,
        lines: [{
          merchandiseId: this.toVariantGid(variant.id),
          quantity: 1,
          attributes: this.toAttributeList({
            [BOGO_CAMPAIGN_KEY]: campaign.id_campaign,
            [BOGO_TIER_KEY]: match.index,
          }),
        }],
      });
      const { cart: updated, userErrors } = data.cartLinesAdd;
      if (userErrors && userErrors.length) return false;

      const giftLine = updated.lines.edges
        .map(({ node }) => node)
        .find(line => line.attributes.some(a => a.key === BOGO_CAMPAIGN_KEY));
      if (!giftLine) return false;
      if (Number(giftLine.cost.totalAmount.amount) === 0) return true;

      await this._request(CART_LINES_REMOVE_MUTATION, { cartId, lineIds: [giftLine.id] });
      return false;
    }
  }

  window.WimStorefrontClient = WimStorefrontClient;
})();
